import React, {useEffect, useState} from 'react';
import {Page, Card, Checkbox, SkeletonBodyText, DataTable, EmptySearchResult} from "@shopify/polaris";
import {useAppBridge} from '@shopify/app-bridge-react';
import {AxiosError} from "axios";
import {useLocation, useNavigate} from "react-router-dom";

import ConsignmentService, {
    ShopifyConsignmentPreviewResponse
} from "../services/ConsignmentService";
import ShopifyLocationFormatted from "../components/ShopifyLocationFormatted";
import DateTimeDescription from "../components/DateTimeDescription";
import AxiosErrorBanner from "../components/AxiosErrorBanner";
import DateTimeRangePopover from "../components/DateTimeRangePopover";
import ProgressDisplayModal from "../components/ProgressDisplayModal";
import OrderItemsPopover from "../components/OrderItemsPopover";
import {OffsetDateTime} from "../services/CommonModel";
import {showToast} from "../util/ShopifyUtil";
import { SettingsContext } from "../contexts/settings"

import '../styles.scss';

export default function SendOrders() {
    // @ts-ignore
    const [ settingsContext ] = React.useContext(SettingsContext)
    const [ initialising, setInitialising ] = useState(true);
    const [ sendingOrders, setSendingOrders ] = useState(false);
    const [ consignmentPreviews, setConsignmentPreviews ] = useState<ShopifyConsignmentPreviewResponse[]>([]);
    const [ axiosError, setAxiosError ] = useState<AxiosError | null>(null);
    const [ deliveryPhotoSelected, setDeliveryPhotoSelected ] = useState<Set<BigInt>>(new Set());
    const [ signatureSelected, setSignatureSelected ] = useState<Set<BigInt>>(new Set());
    const shopifyAppBridge = useAppBridge();
    const navigate = useNavigate();
    const location = useLocation();

    const previewOrders = () => {
        const id = new URLSearchParams(location.search).get('id');
        const ids = new URLSearchParams(location.search).getAll('ids[]');
        if (id) {
            ids.push(id);
        }
        if (ids && ids.length) {
            ConsignmentService.preview(
                ids,
                setConsignmentPreviews,
                setAxiosError,
                () => {
                    setInitialising(false);
                }
            )
        } else {
            setInitialising(false);
        }
    }

    useEffect(() => {
        if (settingsContext.settings.consignmentDefaults.deliveryPhotoRequired) {
            onDeliveryPhotoSelectAll(true);
        }
        if (settingsContext.settings.consignmentDefaults.deliverySignatureRequired) {
            onSignatureSelectAll(true);
        }
    }, [consignmentPreviews]); // eslint-disable-line react-hooks/exhaustive-deps

    const onDeliveryPhotoChecked = (checked: boolean, fulfillmentOrderId: BigInt) => {
        const selected = new Set<BigInt>(deliveryPhotoSelected);
        if (checked) {
            selected.add(fulfillmentOrderId);
        } else {
            selected.delete(fulfillmentOrderId);
        }
        setDeliveryPhotoSelected(selected);
    }

    const onDeliveryPhotoSelectAll = (checked: boolean) => {
        const selected = new Set<BigInt>();
        if (checked) {
            consignmentPreviews.forEach(preview => selected.add(preview.fulfillmentOrderId));
        }
        setDeliveryPhotoSelected(selected);
    }

    const onSignatureChecked = (checked: boolean, fulfillmentOrderId: BigInt) => {
        const selected = new Set<BigInt>(signatureSelected);
        if (checked) {
            selected.add(fulfillmentOrderId);
        } else {
            selected.delete(fulfillmentOrderId);
        }
        setSignatureSelected(selected);
    }

    const onSignatureSelectAll = (checked: boolean) => {
        const selected = new Set<BigInt>();
        if (checked) {
            consignmentPreviews.forEach(preview => selected.add(preview.fulfillmentOrderId));
        }
        setSignatureSelected(selected);
    }

    const onDeliveryTimeChanged = (fulfillmentOrderId: BigInt, from: OffsetDateTime, to: OffsetDateTime) => {
        const previews = consignmentPreviews.map(preview => {
            if (preview.fulfillmentOrderId === fulfillmentOrderId) {
                return {
                    ...preview,
                    deliveryFrom: from,
                    deliveryTo: to
                };
            }
            return preview;
        });
        setConsignmentPreviews(previews);
    }

    const onSendOrdersToSubTrux = () => {
        setSendingOrders(true);
        const options = consignmentPreviews.map(preview => {
            return {
                orderId: preview.orderId,
                fulfillmentOrderId: preview.fulfillmentOrderId,
                deliveryFrom: preview.deliveryFrom,
                deliveryTo: preview.deliveryTo,
                deliveryPhotoRequired: deliveryPhotoSelected.has(preview.fulfillmentOrderId),
                signatureRequired: signatureSelected.has(preview.fulfillmentOrderId)
            }
        });
        const request = {
            fulfillmentOrders: options
        };
        ConsignmentService.create(request,
            response => {},
            setAxiosError,
            () => {
                showToast(shopifyAppBridge, 'Orders were successfully sent to SubTrux', 10000);
                setSendingOrders(false);
                navigate("/");
            }
        );
    }

    useEffect(() => {
        previewOrders();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // passing an empty array as second argument triggers the callback in useEffect only after the initial render thus replicating `componentDidMount` lifecycle behaviour

    return (
        <Page
            fullWidth={true}
            title="Send orders to SubTrux"
            primaryAction={{
                content: 'Send to SubTrux',
                disabled: initialising || consignmentPreviews.length <= 0,
                onAction: onSendOrdersToSubTrux
            }}
        >
            <Card>
                <ProgressDisplayModal
                    visible={sendingOrders}
                    message="Sending orders to SubTrux"
                />
                {initialising &&
                <div style={{padding: '16px'}}>
                    <SkeletonBodyText />
                    <SkeletonBodyText />
                    <SkeletonBodyText />
                </div>
                }
                <AxiosErrorBanner
                    axiosError={axiosError}
                    onDismiss={() => setAxiosError(null)}
                />
                {!initialising && consignmentPreviews.length <= 0 &&
                <EmptySearchResult
                    title={'No orders found'}
                    description={'SubTrux will only show orders within the last 60 days so orders older than that will not show here.'}
                    withIllustration
                />
                }
                {!initialising && consignmentPreviews.length > 0 &&
                <DataTable
                    columnContentTypes={[
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                        'text',
                    ]}
                    headings={[
                        'Order #',
                        'Date',
                        'Customer',
                        'Items',
                        'Pickup Location',
                        'Deliver To',
                        'Delivery Date',
                        'Delivery Photo?',
                        'Signature?',
                    ]}
                    totalsName={{
                        singular: 'Select all',
                        plural: 'Select all',
                    }}
                    totals={[
                        '', // Order #
                        '', // Date
                        '', // Customer
                        '', // Items
                        '', // Pickup Location
                        '', // Deliver To
                        '', // Delivery Date
                        <div className="center-content">
                            <Checkbox
                                label="Delivery Photo"
                                labelHidden
                                checked={deliveryPhotoSelected.size === consignmentPreviews.length}
                                onChange={onDeliveryPhotoSelectAll}
                            />
                        </div>,
                        <div className="center-content">
                            <Checkbox
                                label="Signature"
                                labelHidden
                                checked={signatureSelected.size === consignmentPreviews.length}
                                onChange={onSignatureSelectAll}
                            />
                        </div>, // Items
                    ]}
                    verticalAlign="middle"
                    rows={consignmentPreviews.map(
                        (preview, index) => {
                            return [
                                preview.orderRef,
                                <DateTimeDescription dateTime={preview.orderDate} />,
                                preview.customerName,
                                <OrderItemsPopover orderItems={preview.items} />,
                                <ShopifyLocationFormatted location={preview.pickup} />,
                                <ShopifyLocationFormatted location={preview.delivery} />,
                                <DateTimeRangePopover
                                    from={preview.deliveryFrom}
                                    to={preview.deliveryTo}
                                    onChange={(from, to) => onDeliveryTimeChanged(preview.fulfillmentOrderId, from, to)}
                                />,
                                <div className="center-content">
                                    <Checkbox
                                        label="Delivery Photo"
                                        labelHidden
                                        checked={deliveryPhotoSelected.has(preview.fulfillmentOrderId)}
                                        onChange={checked => onDeliveryPhotoChecked(checked, preview.fulfillmentOrderId)}
                                    />
                                </div>,
                                <div className="center-content">
                                    <Checkbox
                                        label="Signature"
                                        labelHidden
                                        checked={signatureSelected.has(preview.fulfillmentOrderId)}
                                        onChange={checked => onSignatureChecked(checked, preview.fulfillmentOrderId)}
                                    />
                                </div>
                            ]
                        })}
                />}
            </Card>
        </Page>
    );
}
